import { useSuspenseQuery } from '@tanstack/react-query'
import { createContext, type ReactNode, useContext } from 'react'
import { Navigate } from 'react-router'

import { useRouteCompanyId } from '@/hooks/use-route-company-id'
import { companiesQueryOptions } from '@/queries/companies'
import type { Company } from '@/services/fintalk/services/fintalk-backoffice/companies/list'
import type { Bot } from '@/services/fintalk/services/fintalk-backoffice/company-bots/list-by-company'
import type { Project } from '@/services/fintalk/services/fintalk-backoffice/projects/list'

import { useRequiredSession } from '../auth/session/use-required-session'
import {
  type GetCompanyBotsState,
  useGetCompanyBots,
} from './use-get-company-bots'
import {
  type GetCompanyProjectsState,
  useGetCompanyProjects,
} from './use-get-company-projects'

export interface CompanyContextProps {
  company: Company
  projects: Project[]
  projectsStatus: GetCompanyProjectsState
  loadProjects: () => Promise<void>
  updateProjectInProjects: (project: Project) => void
  bots: Bot[]
  botsStatus: GetCompanyBotsState
  loadBots: () => Promise<void>
}

const CompanyContext = createContext<CompanyContextProps | null>(null)

export function CompanyProvider({ children }: { children: ReactNode }) {
  const companyId = useRouteCompanyId()!

  const { userId } = useRequiredSession()

  const { data: company } = useSuspenseQuery({
    ...companiesQueryOptions(userId),
    select: (data) => data.find((company) => company._id === companyId),
  })

  const { projects, projectsStatus, loadProjects, updateProjectInProjects } =
    useGetCompanyProjects()
  const { bots, botsStatus, loadBots } = useGetCompanyBots()

  if (!company) {
    return <Navigate to="/companies" replace />
  }

  return (
    <CompanyContext
      value={{
        company,
        projects,
        projectsStatus,
        loadProjects,
        updateProjectInProjects,
        bots,
        botsStatus,
        loadBots,
      }}
    >
      {children}
    </CompanyContext>
  )
}

/**
 * Reads the current company, its projects and its bots. Consumers that only need the
 * id must use useRouteCompanyId() instead: the URL is the source of truth for
 * which company is selected, this provider only carries its data.
 */
export const useCompany = () => {
  const context = useContext(CompanyContext)

  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider')
  }

  return context
}
